import React, { useEffect, useState } from 'react';
import { Comp, CompLineItem } from './comp.type';
import { Division } from '../glossary/glossary.type';
import Card from '../general/Card';
import { Link, useNavigate } from 'react-router-dom';
import { Grid, Alert, Stack } from '@mui/material';
import { Box, Button, Typography } from 'components/index';
import { AttachMoney, Save, CorporateFare, Launch } from '@mui/icons-material';
import { flattenArray, formatCurrency, joinDateAndUser } from '../../common/utils/common.util';
import { ProjectCostPerUnit, ReportGeneralInfo } from '../report/report.type';
import Footer from '../general/Footer';
import { usePrompt } from '../AppPrompt';
import { useQueryClient } from 'react-query';
import keys from '../../common/react-query/data-keys';
import { DataGrid } from '@mui/x-data-grid';
import { useAppContext } from 'common/AppContext';
import { REPORT_COLORS, HEIGHTS } from '../report/constants';
import { UNDEFINED_CHANGE_ORDER_DIVISION_CODE } from '../../common/constants/general';
import { getTotalsRow } from '../report/report.util';
import { getReportColumns } from '../report/ReportColumns';
import useCompDivisionDraws from './useCompDivisionDraws';
import { ProjectDraw } from '../project/project.type';
import CompDivisionTable from './CompDivisionTable';
import ProjectDrawSelectionDialog from '../report/ProjectDrawSelectionDialog';
import { updateDivisionDraws } from './comp.api';
import { getProjectAddressString } from '../project/project.util';
import { ROUTES } from 'common/constants/urls';
import GeneralInfoCard from '../report/GeneralInfoCard';
import CompLineItemEditDialog from './CompLineItemEditDialog';
import ReadOnlyCoverImage from '../common/CoverImage/ReadOnlyCoverImage';

interface Props {
  comp: Comp;
  divisions: Division[];
  compDraws: ProjectDraw[];
  isCompDirty: boolean;
  isCompLoading: boolean;
  updateComp: (updatedComp: Comp) => void;
  resetComp: () => void;
  saveComp: () => void;
  refetchComp: () => void;
}

const CompRenderer: React.FC<Props> = ({
  comp,
  divisions,
  compDraws,
  isCompDirty,
  isCompLoading,
  updateComp,
  resetComp,
  saveComp,
  refetchComp,
}) => {
  const navigate = useNavigate();
  const { getConfirmation } = usePrompt();
  const queryClient = useQueryClient();
  const { setPageHeaderText } = useAppContext();
  const [generalInfoDialogOpen, setGeneralInfoDialogOpen] = useState(false);
  const [divisionTables, setDivisionTables] = useState<{ [key: string]: any[] }>({});
  const [selectedLineItem, setSelectedLineItem] = useState<CompLineItem | null>(null);
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null);
  const [lineItemEditDialogOpen, setLineItemEditDialogOpen] = useState(false);
  const { selectedProjectDraws, handleProjectDrawSelectionChange } = useCompDivisionDraws(comp.id);

  useEffect(() => {
    if (comp.name) {
      setPageHeaderText(comp.name);
    }
    return () => {
      setPageHeaderText('');
    };
  }, [comp.name]);

  const handleDrop = (draggedItem: any, targetDivision: Division | undefined) => {
    if (!targetDivision) return;

    const rowsToMove: CompLineItem[] =
      draggedItem.selectedRows && Array.isArray(draggedItem.selectedRows)
        ? draggedItem.selectedRows
        : draggedItem.fullRow
          ? [draggedItem.fullRow]
          : [];

    if (!rowsToMove.length) return;

    const modifiedLineItems: CompLineItem[] = rowsToMove.map(row => ({
      ...row,
      division: targetDivision,
      isEdited: true,
      divisionId: targetDivision.id,
    }));

    const updatedLineItems = comp.lineItems.map(lineItem => modifiedLineItems.find(modified => modified.id === lineItem.id) || lineItem);

    updateComp({ ...comp, lineItems: updatedLineItems });
  };

  const totalBudget = React.useMemo(() => {
    return comp.lineItems.filter(li => !li.isDeleted).reduce((acc, row) => acc + row.totalBudget, 0);
  }, [comp.lineItems]);

  const costPerUnit: ProjectCostPerUnit = React.useMemo(() => {
    const grossBuildingSF = comp?.grossBuildingSF ?? 0;
    const netRentableSF = comp?.netRentableSF ?? 0;
    const buildingCount = comp?.buildingCount ?? 0;
    const storyCount = comp?.storyCount ?? 0;
    const unitCount = comp?.unitCount ?? 0;
    const siteAcreage = comp?.siteAcreage ?? 0;

    return {
      buildingGSF: grossBuildingSF > 0 ? totalBudget / grossBuildingSF : 0,
      buildingNRSF: netRentableSF > 0 ? totalBudget / netRentableSF : 0,
      building: buildingCount > 0 ? totalBudget / buildingCount : 0,
      story: storyCount > 0 ? totalBudget / storyCount : 0,
      unit: unitCount > 0 ? totalBudget / unitCount : 0,
      acre: siteAcreage > 0 ? totalBudget / siteAcreage : 0,
    };
  }, [comp, totalBudget]);

  // Arrange line items into division tables
  useEffect(() => {
    categorizeLineItemsIntoDivisions(comp, divisions, setDivisionTables);
  }, [divisions, comp]);

  const sortedDivisions = React.useMemo(() => {
    return [...divisions].sort((a, b) => Number(a.code) - Number(b.code));
  }, [divisions]);

  const sortedTables = React.useMemo(() => {
    const sortedTables: any = [];
    sortedDivisions.forEach(division => {
      sortedTables.push(divisionTables[division.id] && divisionTables[division.id].length > 0 ? divisionTables[division.id] : []);
    });
    return sortedTables;
  }, [divisionTables, sortedDivisions]);

  const hardCostSubTotalRow = React.useMemo(() => {
    return [getTotalsRow('HARD COST SUBTOTAL', flattenArray(sortedTables, 0, 15), comp?.grossBuildingSF ?? 0, 'no-division')];
  }, [sortedTables, comp]);

  const projectedHardCostSubTotalRow = React.useMemo(() => {
    const contractor_overhead_and_contingency = [...sortedTables[16], ...sortedTables[17]];
    const projectedTotalHardCost = [...contractor_overhead_and_contingency, ...hardCostSubTotalRow];
    return [getTotalsRow('TOTAL PROJECTED HARD COSTS', projectedTotalHardCost, comp?.grossBuildingSF ?? 0, 'no-division')];
  }, [sortedTables, comp]);

  const softCostSubTotalRow = React.useMemo(() => {
    return [getTotalsRow('TOTAL SOFT COSTS', [...sortedTables[18], ...sortedTables[19]], comp?.grossBuildingSF ?? 0, 'no-division')];
  }, [sortedTables, comp]);

  const totalProjectCostsRow = React.useMemo(() => {
    return [
      getTotalsRow(
        'TOTAL PROJECTED COSTS',
        [...projectedHardCostSubTotalRow, ...softCostSubTotalRow],
        comp?.grossBuildingSF ?? 0,
        'no-division',
      ),
    ];
  }, [sortedTables, comp]);

  const generalInfo = React.useMemo(() => {
    return {
      grossBuildingSF: comp.grossBuildingSF,
      netRentableSF: comp.netRentableSF,
      buildingCount: comp.buildingCount,
      storyCount: comp.storyCount,
      unitCount: comp.unitCount,
      siteAcreage: comp.siteAcreage,
      elevatorCount: comp?.project?.elevatorCount || undefined,
    };
  }, [comp]);

  const updateLineItem = (updatedLineItem: CompLineItem) => {
    const updatedLineItems = comp.lineItems.map(lineItem => {
      if (lineItem.id === updatedLineItem.id) {
        return { ...updatedLineItem, isEdited: true };
      }
      return lineItem;
    });
    updateComp({ ...comp, lineItems: updatedLineItems });
  };

  const divisionTablesRendered = React.useMemo(() => {
    const tablesRendered = sortedDivisions.map((division, index) => {
      const gridData = sortedTables[index];
      return (
        <React.Fragment key={division.id}>
          <CompDivisionTable
            key={division.id}
            handleDrop={handleDrop}
            division={division}
            gridData={gridData}
            divisions={divisions}
            comp={comp}
            updateComp={updateComp}
            updateLineItem={updateLineItem}
            setSelectedLineItem={setSelectedLineItem}
            setLineItemEditDialogOpen={setLineItemEditDialogOpen}
            handleOpenProjectDialog={setSelectedDivision}
            refetchComp={refetchComp}
            isCompDirty={isCompDirty}
            hardCostSubTotal={projectedHardCostSubTotalRow[0].totalBudget}
            reportGeneralInfo={generalInfo}
          />
          {index == 15 ? (
            <Grid size={{ xs: 12 }} key={'hard-cost-totals'} sx={{ minHeight: HEIGHTS.SUBTOTAL_HEIGHT }}>
              <DataGrid
                rows={hardCostSubTotalRow}
                columns={getReportColumns(null, null, true)}
                checkboxSelection={true}
                isCellEditable={() => false}
                hideFooter
                sx={{
                  ...reportTotalsCommonStyles,
                  '& .projectHeader': {
                    backgroundColor: REPORT_COLORS.HARD_COST_SUBTOTAL_COLOR,
                  },
                  '& .projectTotal': {
                    backgroundColor: REPORT_COLORS.HARD_COST_SUBTOTAL_COLOR,
                  },
                }}
              />
            </Grid>
          ) : null}
          {index == 17 ? (
            <Grid size={{ xs: 12 }} key={'projected-hard-cost-totals'} sx={{ minHeight: HEIGHTS.SUBTOTAL_HEIGHT }}>
              <DataGrid
                rows={projectedHardCostSubTotalRow}
                columns={getReportColumns(null, null, true)}
                checkboxSelection={true}
                isCellEditable={() => false}
                hideFooter
                sx={{
                  ...reportTotalsCommonStyles,
                  '& .projectHeader': {
                    backgroundColor: REPORT_COLORS.PROJECTED_HARD_COST_SUBTOTAL_COLOR,
                  },
                  '& .projectTotal': {
                    backgroundColor: REPORT_COLORS.PROJECTED_HARD_COST_SUBTOTAL_COLOR,
                  },
                }}
              />
            </Grid>
          ) : null}
          {index == 19 ? (
            <Grid size={{ xs: 12 }} key={'soft-cost-totals'} sx={{ minHeight: HEIGHTS.SUBTOTAL_HEIGHT }}>
              <DataGrid
                rows={softCostSubTotalRow}
                columns={getReportColumns(null, null, true)}
                checkboxSelection={true}
                isCellEditable={() => false}
                hideFooter
                sx={{
                  ...reportTotalsCommonStyles,
                  '& .projectHeader': {
                    backgroundColor: REPORT_COLORS.SOFT_COST_SUBTOTAL_COLOR,
                  },
                  '& .projectTotal': {
                    backgroundColor: REPORT_COLORS.SOFT_COST_SUBTOTAL_COLOR,
                  },
                }}
              />
            </Grid>
          ) : null}
        </React.Fragment>
      );
    });
    tablesRendered.push(
      <Grid size={{ xs: 12 }} key={'total-project-costs'} sx={{ minHeight: HEIGHTS.SUBTOTAL_HEIGHT }}>
        <DataGrid
          rows={totalProjectCostsRow}
          columns={getReportColumns(null, null, true)}
          checkboxSelection={true}
          isCellEditable={() => false}
          hideFooter
          sx={{
            ...reportTotalsCommonStyles,
            '& .projectHeader': {
              backgroundColor: REPORT_COLORS.TOTAL_COST_COLOR,
            },
            '& .projectTotal': {
              backgroundColor: REPORT_COLORS.TOTAL_COST_COLOR,
            },
          }}
        />
      </Grid>,
    );

    // Handling "No Division" data grid
    tablesRendered.unshift(
      <CompDivisionTable
        key={'no-division'}
        handleDrop={handleDrop}
        division={null}
        gridData={divisionTables['no-division'] ?? []}
        divisions={divisions}
        comp={comp}
        refetchComp={refetchComp}
        updateComp={updateComp}
        updateLineItem={updateLineItem}
        setSelectedLineItem={setSelectedLineItem}
        setLineItemEditDialogOpen={setLineItemEditDialogOpen}
        handleOpenProjectDialog={setSelectedDivision}
        isCompDirty={isCompDirty}
        reportGeneralInfo={generalInfo}
      />,
    );

    return tablesRendered;
  }, [divisionTables, sortedDivisions, sortedTables, comp]);

  const updateCompMetadata = (generalInfo: ReportGeneralInfo) => {
    updateComp({
      ...comp,
      name: generalInfo.reportName ?? '',
      grossBuildingSF: generalInfo.grossBuildingSF,
      netRentableSF: generalInfo.netRentableSF,
      buildingCount: generalInfo.buildingCount,
      storyCount: generalInfo.storyCount,
      unitCount: generalInfo.unitCount,
      siteAcreage: generalInfo.siteAcreage,
      isEditedLocally: true,
    });
  };

  const updateCompDivisionDraws = async (divisionId: string, newDrawsIds: string[]) => {
    await updateDivisionDraws(comp.id, {
      divisionId,
      newProjectDrawIds: newDrawsIds,
    });
    queryClient.invalidateQueries([keys.comps]);
  };

  // Add a check for cover image existence
  const hasCoverImage = Boolean(comp.project.previewCoverImage?.blobName);

  return (
    <>
      {comp.errorMessage ? <Alert severity='error'>{comp.errorMessage}</Alert> : null}
      <Box>
        <Alert severity='warning' sx={{ my: 2, mx: 'auto' }}>
          Changes to this comparable will only be reflected in the respective cost analysis!
        </Alert>
      </Box>
      <Grid container spacing={2} pb={2}>
        <Grid container size={{ xs: hasCoverImage ? 8 : 12 }} spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Card
              icon={<CorporateFare color='primary' />}
              heading={
                <Stack direction={'row'} alignItems={'center'}>
                  <Typography variant='h5' color='primary'>
                    Project Info
                  </Typography>
                </Stack>
              }
              data={{
                'Project Title': comp.project.projectTitle ? (
                  <Link
                    to={`${ROUTES.PROJECTS}/${comp.project.projectId}`}
                    target='_blank'
                    style={{ textDecoration: 'none', color: 'unset', flexGrow: 1 }}
                  >
                    <Typography variant='body2' fontWeight='regular' textTransform='capitalize'>
                      {comp.project.projectTitle}
                      {<Launch sx={{ fontSize: '16px', ml: '5px' }} color='primary' />}
                    </Typography>
                  </Link>
                ) : (
                  '--'
                ),
                'Project Number': comp.project.projectNumber || '--',
                'Project Address': getProjectAddressString(comp.project.projectAddress) || '--',
              }}
            />
          </Grid>
          <Grid size={{ xs: hasCoverImage ? 6 : 6 }}>
            <GeneralInfoCard
              generalInfo={{
                reportName: comp.name,
                grossBuildingSF: comp.grossBuildingSF,
                netRentableSF: comp.netRentableSF,
                buildingCount: comp.buildingCount,
                storyCount: comp.storyCount,
                unitCount: comp.unitCount,
                siteAcreage: comp.siteAcreage,
                isEdited: comp.isEditedLocally,
              }}
              updateGeneralInfo={updateCompMetadata}
              cardProps={{
                itemsPerRow: 2,
              }}
            />
          </Grid>
          <Grid size={{ xs: hasCoverImage ? 6 : 6 }}>
            <Card
              icon={<AttachMoney color='primary' />}
              heading='Cost Per Unit'
              backgroundColor={REPORT_COLORS.BLUE}
              data={{
                'Building GSF': costPerUnit.buildingGSF != 0 ? formatCurrency(costPerUnit.buildingGSF.toString()) : '--',
                'Building NRSF': costPerUnit.buildingNRSF != 0 ? formatCurrency(costPerUnit.buildingNRSF.toString()) : '--',
                Building: costPerUnit.building != 0 ? formatCurrency(costPerUnit.building.toString()) : '--',
                Story: costPerUnit.story != 0 ? formatCurrency(costPerUnit.story.toString()) : '--',
                Acre: costPerUnit.acre != 0 ? formatCurrency(costPerUnit.acre.toString()) : '--',
                Unit: costPerUnit.unit != 0 ? formatCurrency(costPerUnit.unit.toString()) : '--',
              }}
              itemsPerRow={2}
            />
          </Grid>
        </Grid>
        {hasCoverImage && (
          <Grid size={{ xs: 4 }} sx={{ display: 'flex' }}>
            <Box
              sx={{
                flexGrow: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ReadOnlyCoverImage
                previewBlobName={comp.project.previewCoverImage?.blobName || null}
                coverImageBlobName={comp.project.coverImage?.blobName || null}
              />
            </Box>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ overflow: 'auto' }}>
        {divisionTablesRendered}
      </Grid>
      <Grid size={{ xs: 12 }} key='grandTotals' sx={{ mt: 2 }}>
        <Card
          icon={<AttachMoney color='primary' />}
          heading='Grand Totals'
          data={{
            Budget: formatCurrency(totalBudget.toString()),
          }}
        />
      </Grid>
      <Footer height={HEIGHTS.FOOTER_HEIGHT}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1}>
          <Stack direction={'row'} spacing={1}>
            <Typography variant='subtitle2'>{joinDateAndUser(comp.createdOn, comp.createdBy, 'Generated on ')}</Typography>
          </Stack>
          <Stack direction={'row'} spacing={1}>
            {isCompDirty ? <Alert severity='warning'>Report has unsaved changes</Alert> : null}
          </Stack>
          <Stack direction={'row'} spacing={1}>
            <Button variant='contained' onClick={saveComp} disabled={!isCompDirty} startIcon={<Save />} loading={isCompLoading}>
              Submit Changes
            </Button>
          </Stack>
        </Stack>
      </Footer>
      <CompLineItemEditDialog
        open={lineItemEditDialogOpen}
        onClose={() => setLineItemEditDialogOpen(false)}
        lineItem={selectedLineItem}
        updateLineItem={updateLineItem}
        divisions={divisions}
      />
      {selectedDivision ? (
        <ProjectDrawSelectionDialog
          open={selectedDivision != null}
          onClose={() => setSelectedDivision(null)}
          divisionId={selectedDivision.id}
          divisionCode={selectedDivision.code}
          divisionName={selectedDivision.name}
          allProjectDraws={compDraws}
          selectedProjectDraws={selectedProjectDraws}
          onSelectionChange={handleProjectDrawSelectionChange}
          updateDivisionDraws={updateCompDivisionDraws}
          isReportDirty={isCompDirty}
        />
      ) : null}
    </>
  );
};

const categorizeLineItemsIntoDivisions = (comp: Comp, divisions: Division[], setDivisionTables: any) => {
  const lineItems = comp.lineItems?.filter(li => !li.isDeleted) || [];

  const newDivisionData: { [key: string]: any[] } = {};

  divisions.forEach(division => {
    const divisionItems = lineItems.filter(item => {
      if (item.division) return item.division.id === division.id;
    });

    if (divisionItems.length > 0) {
      newDivisionData[division.id] = divisionItems.map(item => ({
        ...item,
        divisionName: division.name,
        isDivisionHeader: false,
      }));
    } else {
      newDivisionData[division.id] = [];
    }
  });

  const noDivisionItems = lineItems.filter(item => !item.division || item.division.code === UNDEFINED_CHANGE_ORDER_DIVISION_CODE);
  if (noDivisionItems.length > 0) {
    newDivisionData['no-division'] = noDivisionItems.map(item => ({
      ...item,
      divisionName: 'No Division',
      isDivisionHeader: false,
    }));
  } else {
    newDivisionData['no-division'] = [];
  }

  setDivisionTables(newDivisionData);
};

const reportTotalsCommonStyles = {
  margin: '0px',
  fontSize: '0.7em',
  '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root, & .MuiDataGrid-columnHeaders .MuiCheckbox-root': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeader': {
    backgroundColor: REPORT_COLORS.WHITE,
    borderBottom: 'none',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    height: 'auto',
    textOverflow: 'clip',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-cellContent': {
    whiteSpace: 'normal',
    lineHeight: 1.2,
    height: 'auto',
    textOverflow: 'clip',
    maxHeight: 'unset !important',
  },
  '& .MuiDataGrid-row, & .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected, & .MuiDataGrid-row.Mui-selected:hover': {
    backgroundColor: REPORT_COLORS.WHITE,
    fontWeight: 600,
  },
  '& .bolderTotalsDescription': {
    fontWeight: 1000,
  },
  '& .compHeader': {
    backgroundColor: REPORT_COLORS.BLUE,
    borderTop: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  '& .compTotal': {
    backgroundColor: REPORT_COLORS.BLUE,
  },
  '& .separatorCol': {
    borderInline: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
    borderBlock: 'none',
  },
  '& .leftBorder': {
    borderLeft: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  '& .rightBorder': {
    borderRight: '0.1px solid',
    borderColor: REPORT_COLORS.BORDER,
  },
  '.MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
    flexDirection: 'row',
  },
};

export default CompRenderer;
